<script setup lang="ts">
	import { ref, onMounted, onUnmounted } from "vue";
	import { auth } from "@/auth";
	import { couldNot } from "@/helpers/userMessages";

	interface ReportLoadedPayload {
		title: string | null;
		canEdit: boolean;
		canManage: boolean;
		hasRichContent: boolean;
		// Whether the embed page can open its Sparky chat drawer (the org-wide
		// reportChatEnabled flag). The drawer lives inside the iframe, so this
		// is the only way the host can know whether to offer a Chat button.
		// Older embed builds omit it — treat a missing field as "no chat".
		chatEnabled?: boolean;
	}

	const DEFAULT_INIT_TIMEOUT_MS = 15000;
	const SAVE_TIMEOUT_MS = 5000;
	const COPY_TIMEOUT_MS = 5000;
	const CANCEL_TIMEOUT_MS = 5000;
	// Same magnitude as CANCEL_TIMEOUT_MS: requestChat mirrors requestCancel's
	// ack-timeout pattern (review finding #3) — but NOT its safe-state part. Unlike
	// requestCancel's timeout, which synthesizes a concrete fallback
	// (`emit("editModeChanged", false)`), requestChat's timeout deliberately emits a
	// stateless `chat-timeout` instead of guessing open/busy (see requestChat below;
	// round-3 review finding #10).
	const CHAT_TIMEOUT_MS = 5000;

	const parsePositiveInteger = (value: unknown): number | null => {
		if (typeof value !== "string" && typeof value !== "number") return null;
		const parsed = Number(value);
		if (!Number.isFinite(parsed) || parsed <= 0) return null;
		return Math.floor(parsed);
	};

	const getInitTimeoutMs = (): number =>
		parsePositiveInteger((window as any).env?.VITE_SCRIBE_INIT_TIMEOUT) ?? DEFAULT_INIT_TIMEOUT_MS;

	const props = defineProps<{
		reportId: string;
		initialChatClosed?: boolean;
	}>();

	const emit = defineEmits<{
		(e: "loaded", data: ReportLoadedPayload): void;
		(e: "saved"): void;
		(e: "dirty", isDirty: boolean): void;
		(e: "error", data: { message: string; code: string }): void;
		(e: "tableCopied"): void;
		(e: "tableDownloaded"): void;
		(e: "copied"): void;
		(e: "copyFailed"): void;
		(e: "editModeChanged", active: boolean): void;
		(e: "chat-state", data: { open: boolean; busy: boolean }): void;
		// Emitted when the embed never acknowledges scribe:chat within CHAT_TIMEOUT_MS.
		// Deliberately carries no state: a lost ack tells us nothing about whether chat
		// ended up open/busy, so we must not guess (review finding F5) — the host only
		// uses this to unstick its own pending-toggle flag.
		(e: "chat-timeout"): void;
	}>();

	const iframeRef = ref<HTMLIFrameElement | null>(null);
	const loading = ref(true);
	// Set when the embed page fails to load (transport/init timeout/scribe:error).
	// The dialog no longer falls back to the legacy viewer, so the frame shows its
	// own error state instead of leaving a blank pane. Mirrors contact-mobile's
	// ScribeReportWebView error UX.
	const errorMessage = ref<string | null>(null);
	const iframeBaseUrl = (window as any).env?.VITE_SCRIBE_ADMIN_URL?.replace(/\/+$/, "") || "";

	let initTimeout: ReturnType<typeof setTimeout> | null = null;
	let saveTimeout: ReturnType<typeof setTimeout> | null = null;
	let copyTimeout: ReturnType<typeof setTimeout> | null = null;
	let cancelTimeout: ReturnType<typeof setTimeout> | null = null;
	let chatTimeout: ReturnType<typeof setTimeout> | null = null;
	let scrollRafId: number | null = null;
	let pendingSaveResolve: ((success: boolean) => void) | null = null;
	let pendingCopyResolve: ((success: boolean) => void) | null = null;
	// Per-request token for requestChat's ack timeout (round-3 finding #5). Bumped on
	// every requestChat() call; the timeout callback captures the token it was armed
	// with and only fires if that token is still the current one, so a request
	// superseded by a newer one can never have its timeout fire on the newer
	// request's behalf.
	let chatRequestToken = 0;
	// The single outstanding chat-toggle request, correlated by its requested `open`
	// target — the scribe:chat/scribe:chat-state protocol carries no request id, so
	// this is the only way to tell a real ack apart from an unsolicited chat-state
	// message (e.g. a busy-flip the embed pushes on its own). ScribeReportDialog.vue's
	// handleChatState applies the SAME correlation rule (matching against its own
	// pendingChatTarget) to decide whether to clear chatTogglePending; the two sides
	// must stay symmetric. Bugbot follow-up to review findings #3/#5: this frame-side
	// half used to clear the ack timeout on EVERY scribe:chat-state regardless of
	// target, so an unsolicited non-matching message could cancel the timeout while
	// the dialog's pending latch (correctly) stayed set — with the timeout gone,
	// nothing was left to ever emit chat-timeout and unstick it, wedging the Chat
	// button for the rest of the dialog session. A non-matching message must now
	// leave the timeout armed so it still fires. Stores only the target, not a
	// duplicate token: requestChat() bumps chatRequestToken and (re)writes this in
	// the same synchronous call, so whenever this is non-null it's already the
	// current request — the timeout callback's own captured `requestToken` closure
	// (below) is the only place a token comparison is ever meaningful.
	let pendingChatRequest: boolean | null = null;
	let pendingLoadedPayload: ReportLoadedPayload | null = null;

	const emitError = (message: string, code: string) => {
		clearInitTimeout();
		pendingLoadedPayload = null;
		loading.value = false;
		errorMessage.value = message;
		// The frame isn't remounted on error — it swaps to its own in-frame error pane
		// in place — so a chat-toggle ack timeout armed before this fired would
		// otherwise still be ticking and later fire a misleading "Sparky's chat didn't
		// respond" toast on top of the load-failure one. Clear it the same way
		// onUnmounted does.
		clearChatTimeout();
		pendingChatRequest = null;
		// Round-11 break-it F5: finish what the chat-timeout fix started — cancel and
		// save were left armed here despite the identical rationale (the frame is
		// staying mounted in its error pane; nothing further will ever arrive to
		// legitimately clear them). Consequence was benign today only because
		// handleFrameError already hard-resets the host's own edit state, but leaving
		// these two timers ticking is the same latent class of bug and the asymmetry
		// with chat was unexplained.
		clearCancelTimeout();
		clearSaveTimeout();
		if (pendingSaveResolve) {
			pendingSaveResolve(false);
			pendingSaveResolve = null;
		}
		emit("error", { message, code });
	};

	const clearInitTimeout = () => {
		if (initTimeout) {
			clearTimeout(initTimeout);
			initTimeout = null;
		}
	};

	const clearSaveTimeout = () => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			saveTimeout = null;
		}
	};

	const clearCopyTimeout = () => {
		if (copyTimeout) {
			clearTimeout(copyTimeout);
			copyTimeout = null;
		}
	};

	const clearCancelTimeout = () => {
		if (cancelTimeout) {
			clearTimeout(cancelTimeout);
			cancelTimeout = null;
		}
	};

	const clearChatTimeout = () => {
		if (chatTimeout) {
			clearTimeout(chatTimeout);
			chatTimeout = null;
		}
	};

	const resolvePendingCopy = (success: boolean) => {
		clearCopyTimeout();
		if (pendingCopyResolve) {
			pendingCopyResolve(success);
			pendingCopyResolve = null;
		}
	};

	const writeCopyContentToClipboard = async (html: unknown, text: unknown) => {
		if (typeof html !== "string" || typeof text !== "string" || !navigator.clipboard) {
			emit("copyFailed");
			resolvePendingCopy(false);
			return;
		}

		try {
			if (typeof ClipboardItem !== "undefined" && typeof navigator.clipboard.write === "function") {
				await navigator.clipboard.write([
					new ClipboardItem({
						"text/html": new Blob([html], { type: "text/html" }),
						"text/plain": new Blob([text], { type: "text/plain" })
					})
				]);
			} else if (typeof navigator.clipboard.writeText === "function") {
				await navigator.clipboard.writeText(text);
			} else {
				throw new Error("Clipboard API is not available");
			}
			emit("copied");
			resolvePendingCopy(true);
		} catch {
			try {
				await navigator.clipboard.writeText(text);
				emit("copied");
				resolvePendingCopy(true);
			} catch (error) {
				console.error("[ScribeReportFrame] clipboard copy failed in parent context", error);
				emit("copyFailed");
				resolvePendingCopy(false);
			}
		}
	};

	const getIframeOrigin = (): string | null => {
		if (!iframeBaseUrl) {
			return null;
		}

		try {
			return new URL(iframeBaseUrl).origin;
		} catch (error) {
			console.error("Invalid Scribe admin URL configured for embedded reports.", error);
			return null;
		}
	};

	const isOriginAllowed = (origin: string): boolean => {
		const iframeOrigin = getIframeOrigin();
		if (iframeOrigin && origin === iframeOrigin) {
			return true;
		}

		return !!import.meta.env.DEV && /^http:\/\/localhost(:\d+)?$/.test(origin);
	};

	const sendViewport = () => {
		const iframe = iframeRef.value;
		const iframeOrigin = getIframeOrigin();
		if (!iframe || !iframe.contentWindow || !iframeOrigin) {
			return;
		}

		const rect = iframe.getBoundingClientRect();
		const visibleTop = Math.max(0, -rect.top);
		const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
		iframe.contentWindow.postMessage({ type: "scribe:viewport", visibleTop, visibleHeight }, iframeOrigin);
	};

	const onScroll = () => {
		if (scrollRafId) {
			return;
		}

		scrollRafId = requestAnimationFrame(() => {
			scrollRafId = null;
			sendViewport();
		});
	};

	const finishLoading = (data: ReportLoadedPayload) => {
		clearInitTimeout();
		loading.value = false;
		sendViewport();
		emit("loaded", data);
	};

	const sendInit = async (targetOrigin: string) => {
		try {
			const token = await auth.getAccessToken();
			if (!token) {
				emitError("Unable to authenticate the embedded report viewer.", "AUTH_FAILED");
				return;
			}

			iframeRef.value?.contentWindow?.postMessage({ type: "scribe:init", token, version: 1 }, targetOrigin);
		} catch (error) {
			console.error("Failed to initialize embedded report viewer.", error);
			emitError("Unable to authenticate the embedded report viewer.", "AUTH_FAILED");
		}
	};

	const onMessage = (event: MessageEvent) => {
		if (!iframeRef.value || event.source !== iframeRef.value.contentWindow) {
			return;
		}

		const data = event.data;
		if (!data || typeof data.type !== "string" || !data.type.startsWith("scribe:")) {
			return;
		}

		if (!isOriginAllowed(event.origin)) {
			return;
		}

		switch (data.type) {
			case "scribe:ready":
				sendInit(event.origin);
				break;
			case "scribe:loaded":
				if (pendingLoadedPayload) break;
				clearInitTimeout();
				// Each Scribe load auto-opens chat; observe that state before asking it to close.
				if (props.initialChatClosed && data.data?.chatEnabled === true) {
					loading.value = true;
					pendingLoadedPayload = data.data as ReportLoadedPayload;
					initTimeout = setTimeout(() => {
						console.warn("Scribe iframe did not announce its initial chat state within timeout.");
						emitError(couldNot("open this report"), "CHAT_INIT_TIMEOUT");
					}, CHAT_TIMEOUT_MS);
				} else {
					finishLoading(data.data as ReportLoadedPayload);
				}
				break;
			case "scribe:saved":
				clearSaveTimeout();
				emit("saved");
				if (pendingSaveResolve) {
					pendingSaveResolve(true);
					pendingSaveResolve = null;
				}
				break;
			case "scribe:save-failed":
				// Dedicated failure signal from the iframe: the save attempt
				// completed (no timeout) but the backend rejected it. Unlike
				// scribe:error, this does NOT trigger the legacy-viewer
				// fallback — the iframe is still healthy, just the save
				// call failed.
				clearSaveTimeout();
				if (pendingSaveResolve) {
					pendingSaveResolve(false);
					pendingSaveResolve = null;
				}
				break;
			case "scribe:dirty":
				emit("dirty", !!data.isDirty);
				break;
			case "scribe:error":
				clearInitTimeout();
				emitError(
					typeof data.message === "string" ? data.message : "Failed to load report.",
					typeof data.code === "string" ? data.code : "UNKNOWN_ERROR"
				);
				break;
			case "scribe:resize":
				break;
			case "scribe:table-copied":
				emit("tableCopied");
				break;
			case "scribe:table-downloaded":
				emit("tableDownloaded");
				break;
			case "scribe:copied":
				// Legacy path (pre-LSP-15XXX): older scribe-admin did
				// the clipboard.write() itself. Chrome blocks that in
				// cross-origin iframes due to missing user activation,
				// so newer scribe-admin sends scribe:copy-content
				// instead (handled below) and lets the parent do the
				// write. Kept for rollout back-compat.
				emit("copied");
				resolvePendingCopy(true);
				break;
			case "scribe:copy-content":
				// New path: the iframe handed us the rendered HTML +
				// plain text so we can write to the clipboard from the
				// parent's context, which still has transient user
				// activation from the Copy button click.
				void writeCopyContentToClipboard(data.html, data.text);
				break;
			case "scribe:copy-failed":
				emit("copyFailed");
				resolvePendingCopy(false);
				break;
			case "scribe:edit-mode":
				// Round-11 break-it F2: mirror the identical correlation fix already
				// applied to scribe:chat-state. requestCancel only ever asks for
				// active:false (there's no per-request "target" the way requestChat has),
				// so only a message reporting active:false can be its ack — a
				// non-matching active:true (the embed entering edit mode via a separate
				// requestEdit(), or any other unsolicited edit-mode-entered message) must
				// NOT clear this timeout. Doing so unconditionally was the only thing that
				// ever guaranteed a lost cancel ack still fires its synthesized fallback;
				// with it gone, closeAfterEditModeExit's latch (round-8 ls-heroku-ci #1's
				// whole premise) has nothing left to unstick it.
				if (!data.active) {
					clearCancelTimeout();
				}
				emit("editModeChanged", !!data.active);
				break;
			case "scribe:chat-state": {
				// Sparky chat panel state pushed from the embed page. Correlate on the
				// `open` target the same way ScribeReportDialog.vue's handleChatState does
				// (see pendingChatRequest above): only THIS message — the one whose `open`
				// matches the outstanding request's target — acknowledges requestChat and
				// clears its safety timeout. A non-matching message (an unsolicited
				// busy-flip, say) is still forwarded to the host so chatOpen/chatBusy stay
				// accurate, but must NOT clear the timeout — that would strand the host's
				// pending latch with no ack (real or timed-out) ever able to clear it.
				const incomingOpen = !!data.open;
				// Explicit !== null (not a truthy check): pendingChatRequest is a valid,
				// non-null `false` when the outstanding request is a toggle-to-close.
				const isChatAcknowledgement = pendingChatRequest !== null && incomingOpen === pendingChatRequest;
				if (isChatAcknowledgement) {
					clearChatTimeout();
					pendingChatRequest = null;
				}
				if (pendingLoadedPayload) {
					if (incomingOpen && pendingChatRequest !== false) {
						clearInitTimeout();
						if (!requestChat(false)) emitError(couldNot("open this report"), "CHAT_INIT_FAILED");
					} else if (!incomingOpen && isChatAcknowledgement) {
						const loadedPayload = pendingLoadedPayload;
						pendingLoadedPayload = null;
						finishLoading(loadedPayload);
					}
					if (incomingOpen || !isChatAcknowledgement) break;
				}
				emit("chat-state", { open: incomingOpen, busy: !!data.busy });
				break;
			}
			default:
				// Round-8 (ls-heroku-ci #2): every other case is either handled or
				// deliberately ignored (scribe:resize); an UNRECOGNIZED type reaching
				// here means the embed sent something this version of the protocol
				// doesn't know about — log it so a future protocol-drift bug (a rename,
				// a typo, a new message added on one side but not the other) is
				// triagable from a console/Datadog line instead of silently vanishing.
				console.warn("[ScribeReportFrame] unrecognized scribe message type:", data.type);
		}
	};

	const requestSave = (): Promise<boolean> => {
		const iframeOrigin = getIframeOrigin();
		if (!iframeRef.value?.contentWindow || !iframeOrigin) {
			return Promise.resolve(false);
		}

		// Round-6 break-it C11: resolve any previous in-flight save with false before
		// overwriting pendingSaveResolve/clearing its timeout — otherwise that earlier
		// promise never settles (not on scribe:saved, since only the latest postMessage
		// can get a reply; not on its own timeout, since clearSaveTimeout() below kills
		// it). The dialog's iframeSaving guard normally prevents overlapping calls, but
		// it's force-cleared by resetIframeState()/handleFrameError(), which can reopen
		// this window. Mirrors requestCopy's identical "last click wins" resolution.
		if (pendingSaveResolve) {
			pendingSaveResolve(false);
			pendingSaveResolve = null;
		}
		clearSaveTimeout();

		return new Promise((resolve) => {
			pendingSaveResolve = resolve;
			iframeRef.value?.contentWindow?.postMessage({ type: "scribe:save" }, iframeOrigin);
			saveTimeout = setTimeout(() => {
				if (pendingSaveResolve) {
					pendingSaveResolve(false);
					pendingSaveResolve = null;
				}
			}, SAVE_TIMEOUT_MS);
		});
	};

	const requestPrint = () => {
		const iframeOrigin = getIframeOrigin();
		if (!iframeRef.value?.contentWindow || !iframeOrigin) return;
		iframeRef.value.contentWindow.postMessage({ type: "scribe:print" }, iframeOrigin);
	};

	const requestCopy = (): Promise<boolean> => {
		const iframeOrigin = getIframeOrigin();
		if (!iframeRef.value?.contentWindow || !iframeOrigin) {
			return Promise.resolve(false);
		}

		// Replace any previous in-flight copy — last click wins.
		resolvePendingCopy(false);

		return new Promise((resolve) => {
			pendingCopyResolve = resolve;
			iframeRef.value?.contentWindow?.postMessage({ type: "scribe:copy" }, iframeOrigin);
			copyTimeout = setTimeout(() => {
				resolvePendingCopy(false);
			}, COPY_TIMEOUT_MS);
		});
	};

	const requestEdit = (): boolean => {
		const iframeOrigin = getIframeOrigin();
		if (!iframeRef.value?.contentWindow || !iframeOrigin) {
			// Round-9 finding #3: requestEdit was the one request* method left
			// unhardened — every sibling (requestCancel, requestChat) already returns
			// boolean + warns on this same silent-guard path. Without it, a click on
			// Edit while the frame's contentWindow/origin is unavailable (torn-down
			// iframe, cross-origin detach) produced no edit mode, no toast, and no
			// console line to triage from.
			console.warn("[ScribeReportFrame] requestEdit: no iframe contentWindow/origin available; ignoring.");
			return false;
		}
		// A fresh, explicit Edit supersedes any earlier Cancel whose acknowledgement
		// never arrived. Leaving that Cancel fallback armed would emit editModeChanged(false)
		// three seconds later and close this newly opened editor (LSP-20071).
		clearCancelTimeout();
		iframeRef.value.contentWindow.postMessage({ type: "scribe:edit" }, iframeOrigin);
		return true;
	};

	const requestCancel = (): boolean => {
		const iframeOrigin = getIframeOrigin();
		if (!iframeRef.value?.contentWindow || !iframeOrigin) {
			// Round-8 (ls-heroku-ci #1): mirrors requestChat's guard — report failure
			// instead of silently no-op'ing. ScribeReportDialog.vue's close() latches
			// closeAfterEditModeExit BEFORE calling this; if this guard fires, no
			// scribe:edit-mode ack (real or synthesized) will ever arrive to consume
			// that latch, wedging the close path. Callers must only latch on a `true`
			// return.
			console.warn("[ScribeReportFrame] requestCancel: no iframe contentWindow/origin available; ignoring.");
			return false;
		}
		// Fire-and-forget cancel with a safety timeout: if the iframe
		// never acknowledges the exit with scribe:edit-mode active=false,
		// synthesize the event ourselves so the parent dialog doesn't
		// stay trapped in edit mode (hasPendingChanges keeps it
		// :persistent otherwise). Any real edit-mode reply clears it.
		clearCancelTimeout();
		iframeRef.value.contentWindow.postMessage({ type: "scribe:cancel" }, iframeOrigin);
		cancelTimeout = setTimeout(() => {
			cancelTimeout = null;
			console.warn("Scribe iframe did not acknowledge scribe:cancel within timeout; forcing edit mode off.");
			emit("editModeChanged", false);
		}, CANCEL_TIMEOUT_MS);
		return true;
	};

	const requestChat = (open: boolean): boolean => {
		const iframeOrigin = getIframeOrigin();
		if (!iframeRef.value?.contentWindow || !iframeOrigin) {
			// Silent no-op guard (review finding #4): if the caller latches a pending-toggle
			// flag before checking this return value, a missing contentWindow/origin would
			// strand that flag forever with no way to retry. Report failure so the caller
			// knows not to latch.
			console.warn("[ScribeReportFrame] requestChat: no iframe contentWindow/origin available; ignoring.");
			return false;
		}
		// Mirrors requestCancel: fire-and-forget with a safety timeout. If the iframe never
		// acknowledges with scribe:chat-state, we do NOT know whether chat ended up open/busy
		// (review finding F5) — a delayed-but-real busy state must survive a lost ack. Only
		// emit a lightweight timeout signal so the host can unstick its own pending-toggle
		// flag; any real (target-matching) chat-state reply clears this timeout first — see
		// pendingChatRequest and the scribe:chat-state case above.
		clearChatTimeout();
		const requestToken = ++chatRequestToken;
		pendingChatRequest = open;
		iframeRef.value.contentWindow.postMessage({ type: "scribe:chat", open }, iframeOrigin);
		chatTimeout = setTimeout(() => {
			chatTimeout = null;
			// Round-3 finding #5: only fire for the request this timeout was armed for. A
			// newer requestChat() call clears/replaces this timeout above, so in practice a
			// superseded timeout can't reach this point — but the token check is cheap
			// defense-in-depth against that invariant ever breaking.
			if (requestToken !== chatRequestToken) return;
			pendingChatRequest = null;
			console.warn("Scribe iframe did not acknowledge scribe:chat within timeout.");
			if (pendingLoadedPayload) {
				emitError(couldNot("open this report"), "CHAT_INIT_TIMEOUT");
			} else {
				emit("chat-timeout");
			}
		}, CHAT_TIMEOUT_MS);
		return true;
	};

	defineExpose({ requestSave, requestPrint, requestCopy, requestEdit, requestCancel, requestChat });

	onMounted(() => {
		const iframeOrigin = getIframeOrigin();
		if (!iframeOrigin) {
			emitError("Embedded report viewer is not configured.", "CONFIG_ERROR");
			return;
		}

		window.addEventListener("message", onMessage);
		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onScroll, { passive: true });

		initTimeout = setTimeout(() => {
			emitError("Timed out initializing the embedded report viewer.", "INIT_TIMEOUT");
		}, getInitTimeoutMs());
	});

	onUnmounted(() => {
		window.removeEventListener("message", onMessage);
		window.removeEventListener("scroll", onScroll);
		window.removeEventListener("resize", onScroll);
		clearInitTimeout();
		clearSaveTimeout();
		clearCopyTimeout();
		clearCancelTimeout();
		clearChatTimeout();
		pendingChatRequest = null;
		if (scrollRafId) {
			cancelAnimationFrame(scrollRafId);
		}
		// Resolve any pending save/copy promises to prevent hanging
		if (pendingSaveResolve) {
			pendingSaveResolve(false);
			pendingSaveResolve = null;
		}
		if (pendingCopyResolve) {
			pendingCopyResolve(false);
			pendingCopyResolve = null;
		}
	});
</script>

<template>
	<div class="scribe-report-frame position-relative h-100 w-100 d-flex flex-column min-h-0">
		<div v-if="errorMessage" class="d-flex justify-center align-center text-center pa-8 text-error" style="min-height: 200px">
			{{ errorMessage }}
		</div>
		<template v-else>
			<div v-if="loading" class="d-flex justify-center align-center" style="min-height: 200px">
				<v-progress-circular indeterminate color="primary" />
			</div>
			<iframe
				ref="iframeRef"
				:src="`${iframeBaseUrl}/embed/v2/reports/${props.reportId}?hideReviewFooter=true`"
				:style="{ width: '100%', height: '100%', border: 'none', minHeight: 0, flex: '1 1 0', visibility: loading ? 'hidden' : 'visible' }"
				title="Scribe report viewer"
				allow="clipboard-write" />
		</template>
	</div>
</template>
